/**
 * CreatorVault — Homepage
 *
 * Drop this into: src/pages/Homepage.tsx
 * Component directory: src/components/homepage/
 *
 * Required dependencies:
 *   - framer-motion
 *   - @/lib/utils (cn helper from shadcn/ui)
 *
 * Required CSS import (add to your global stylesheet or here):
 *   import './homepage.css'  — or adjust path as needed
 *
 * Required fonts (add to your <head> or global CSS):
 *   Cabinet Grotesk — https://fontshare.com/fonts/cabinet-grotesk
 *   General Sans   — https://fontshare.com/fonts/general-sans
 *
 * Routing assumptions:
 *   /auth?type=creator  — creator sign-up
 *   /auth?type=brand    — brand sign-up
 *   /pricing            — pricing page
 */

import React, { useEffect } from 'react';

import { useAudienceMode } from '@/hooks/useAudienceMode';

// Custom CSS — handles gradient backgrounds, orb animations, glassmorphism
import './homepage.css';

// Components
import NavBar from './components/NavBar';
import HeroSection from './components/HeroSection';
import ViewToggle from './components/ViewToggle';
import StatsBar from './components/StatsBar';
import FeaturesGrid from './components/FeaturesGrid';
import PlatformBento from './components/PlatformBento';
import CaseStudy from './components/CaseStudy';
import ComparisonTable from './components/ComparisonTable';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

// Declare TikTok global so TypeScript doesn't complain
declare global {
  interface Window {
    trackTikTokEvent?: (event: string) => void;
  }
}

/**
 * Homepage — root page component.
 *
 * Manages:
 *   - `view` state ('creator' | 'brand'), synced to the `?mode=` URL param,
 *     passed down to all sections
 *   - TikTok PageView event on mount
 *   - Section ordering and conditional rendering (CaseStudy brand-only)
 */
const Homepage: React.FC = () => {
  const [view, setView] = useAudienceMode();

  // TikTok pixel PageView event on initial page load
  useEffect(() => {
    if (typeof window.trackTikTokEvent === 'function') {
      window.trackTikTokEvent('PageView');
    }
  }, []);

  return (
    <>
      {/* Fixed navigation — shows auth link for current view */}
      <NavBar view={view} />

      {/* View toggle — placed above hero for clear context */}
      <div className="pt-20 pb-0 flex justify-center" style={{ position: 'relative', zIndex: 2 }}>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {/* Full-viewport hero with orb backgrounds */}
      <HeroSection view={view} />

      {/* Stats bar — 4 key metrics, data switches with view */}
      <StatsBar view={view} />

      {/* 6-card features grid */}
      <FeaturesGrid view={view} />

      {/* Bento grid platform showcase */}
      <PlatformBento view={view} />

      {/* Case study — brand view only */}
      {view === 'brand' && <CaseStudy />}

      {/* Feature comparison table */}
      <ComparisonTable view={view} />

      {/* FAQ accordion */}
      <FAQ />

      {/* Bottom CTA section */}
      <FinalCTA view={view} />

      {/* Footer */}
      <Footer />
    </>
  );
};

export default Homepage;
